// Patrón compartido de curvas de crecimiento — 028B-1/028B-2/028C-1/028C-2
// (numeración corregida 2026-09-15, ver PLAN.md Serie 028; fila 24-27 de
// 8.3-bis): "no hace falta Questionnaire aquí — son series numéricas puras,
// mapeo limpio a Observation, mismo patrón que 034". Un Observation por
// medición (peso, talla, perímetro cefálico), perfil vital-signs estándar.
// Las 4 variantes (niña/niño, <5a/5-9a) solo cambian el título — la curva
// (percentiles) es un cálculo de frontend contra estos puntos, no algo que
// se resuelva acá.
// Puerto de packages/hce/src/hce/forms/curvaCrecimiento.tsx a shadcn/Tailwind
// — `onSubmitted` reemplazado por `onGuardado(pares)`. El título propio
// (order=3 en el original) se omite acá: FormularioDiscretoPage ya muestra
// un título con `hub.formulario.<codigo>` arriba de cada componente — mismo
// criterio que Formulario006/007/117.
import type { Observation, ObservationComponent } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CODIGO_COMPONENT_POSICION_TALLA } from '../../paciente/curvaPercentilPorEdad';
import { GRAFICAS_CON_EJE_X_PROPIO, GRAFICAS_POR_FAMILIA, type FamiliaCurvasControlNino, type TextosCurvasControlNinoPdf } from '../../paciente/curvasControlNinoPdf';
import { buscarCurvasControlNinoPdf } from '../../paciente/curvasControlNinoPdfRemoto';
import type { FormularioDiscretoProps, Par } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { VITAL_SIGNS_CATEGORY } from './vitalSigns';

const PESO_LOINC = '29463-7';
const TALLA_LOINC = '8302-2';
const PERIMETRO_CEFALICO_LOINC = '9843-4';
/** Mismo LOINC que Formulario034.tsx (peso gestacional de la madre) — acá es la edad gestacional AL NACER del bebé (ver PLAN.md Riesgo 9, curvaPercentilPorEdad.ts). */
const EDAD_GESTACIONAL_LOINC = '49051-6';

export interface CurvaCrecimientoConfig {
  readonly claveI18n: string;
  /**
   * Código MSP del formulario (016B/016C). Con él, el PDF firmado lleva el encabezado MSP (bloque A) y la cita
   * `SNS-MSP/HCU-form.<código>/2021`. Sin él (serie 028B/028C) todo sigue como antes: PDF genérico de pares.
   */
  readonly codigo?: string;
  /** 028B-1/028B-2 (2m-5a): el corte OMS longitud(acostado)/talla(de pie) cae a mitad de este rango — 028C (5-9a, todos de pie) no lo necesita. Ver PLAN.md Riesgo 2. */
  readonly incluirPosicionTalla?: boolean;
  /** Solo 028B-1/028B-2 (0-5a) — no aplica a 028C (5-9a, la OMS no publica perímetro cefálico más allá de los 5 años). Ver PLAN.md Serie 028 Sub-tanda C. */
  readonly incluirPerimetroCefalico?: boolean;
  /**
   * `'requerido'` en 028A-1 (Fenton): sin este dato no hay eje X posible (edad
   * postmenstrual). `'opcional'` en 028B-1/028B-2: destraba la edad corregida
   * (Riesgo 9) para el prematuro ocasional, pero la mayoría de los pacientes
   * son de término. `undefined` en 028C (5-9a): irrelevante a esa edad.
   */
  readonly incluirEdadGestacionalAlNacer?: 'requerido' | 'opcional';
  /**
   * Familia de curvas que el PDF firmado dibuja tras guardar (las mismas que la pantalla): `fenton` (016B/016C/028A-1), `oms0a5` (028B-1/028B-2) u
   * `oms5a19` (028C-1/028C-2). Ver `paciente/curvasControlNinoPdf.ts`.
   */
  readonly curvasPdf: FamiliaCurvasControlNino;
}

/** Textos de las gráficas del PDF (i18n `curvaCrecimiento.pdf.<familia>`), en el orden de la familia. */
function textosCurvasPdf<F extends FamiliaCurvasControlNino>(familia: F, t: (clave: string) => string): TextosCurvasControlNinoPdf<F> {
  const graficas = Object.fromEntries(
    GRAFICAS_POR_FAMILIA[familia].map((id) => [
      id,
      {
        titulo: t(`curvaCrecimiento.pdf.${familia}.${id}.titulo`),
        nota: t(`curvaCrecimiento.pdf.${familia}.${id}.nota`),
        ejeX: GRAFICAS_CON_EJE_X_PROPIO.has(id) ? t(`curvaCrecimiento.pdf.${familia}.${id}.ejeX`) : undefined,
      },
    ]),
  );
  const ejeX = familia === 'fenton' ? 'ejeSemanas' : familia === 'oms0a5' ? 'ejeMeses' : 'ejeAnios';
  return { graficas, ejeX: t(`curvaCrecimiento.pdf.${ejeX}`), avisoFentonNinas: familia === 'fenton' ? t('curvaCrecimiento.pdf.avisoFentonNinas') : undefined } as unknown as TextosCurvasControlNinoPdf<F>;
}

export function crearFormularioCurvaCrecimiento(config: CurvaCrecimientoConfig): (props: FormularioDiscretoProps) => JSX.Element {
  return function FormularioCurvaCrecimiento({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
    const { t } = useTranslation();
    const medplum = useMedplum();
    const [peso, setPeso] = useState<number | ''>('');
    const [talla, setTalla] = useState<number | ''>('');
    const [posicionTalla, setPosicionTalla] = useState<'acostado' | 'de-pie' | ''>('');
    const [perimetroCefalico, setPerimetroCefalico] = useState<number | ''>('');
    const [edadGestacionalAlNacer, setEdadGestacionalAlNacer] = useState<number | ''>('');
    const { guardando, error, guardar } = useGuardado();
    const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
    const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, config.codigo ?? '', config.codigo ? t(`hub.formulario.${config.codigo}`) : '', {
      // Curvas de Fenton (prematuros/neonatos): Neonatología, no consulta externa.
      servicio: config.codigo ? 'Neonatología' : undefined,
    });

    const imcCalculado = peso !== '' && talla !== '' && talla > 0 ? peso / ((talla / 100) * (talla / 100)) : undefined;
    const faltaEdadGestacionalRequerida = config.incluirEdadGestacionalAlNacer === 'requerido' && edadGestacionalAlNacer === '';

    const handleSubmit = (): Promise<void> => guardar(async () => {
      const lote = new LoteTransaccional();
      const ahora = new Date().toISOString();
      const componentPosicionTalla: ObservationComponent | undefined =
        config.incluirPosicionTalla && posicionTalla ? { code: { text: CODIGO_COMPONENT_POSICION_TALLA }, valueString: posicionTalla } : undefined;
      const componentEdadGestacional: ObservationComponent | undefined =
        config.incluirEdadGestacionalAlNacer && edadGestacionalAlNacer !== ''
          ? { code: { coding: [{ system: 'http://loinc.org', code: EDAD_GESTACIONAL_LOINC, display: 'Edad gestacional' }] }, valueQuantity: { value: edadGestacionalAlNacer, unit: 'semanas', system: 'http://unitsofmeasure.org', code: 'wk' } }
          : undefined;
      const componentesComunes = [componentEdadGestacional].filter((c): c is ObservationComponent => c !== undefined);
      const mediciones: Array<[number | '', string, string, string, string, string, ObservationComponent[] | undefined]> = [
        [peso, PESO_LOINC, 'Peso corporal', 'kg', 'kg', t('curvaCrecimiento.peso'), componentesComunes],
        [talla, TALLA_LOINC, 'Talla', 'cm', 'cm', t('curvaCrecimiento.talla'), [...componentesComunes, ...(componentPosicionTalla ? [componentPosicionTalla] : [])]],
      ];
      if (config.incluirPerimetroCefalico) {
        mediciones.push([perimetroCefalico, PERIMETRO_CEFALICO_LOINC, 'Perímetro cefálico', 'cm', 'cm', t('curvaCrecimiento.perimetroCefalico'), componentesComunes]);
      }
      const creaciones: Observation[] = [];
      const pares: Par[] = [];
      for (const [valor, loinc, display, unidad, unidadUcum, etiqueta, component] of mediciones) {
        if (valor === '') continue;
        creaciones.push(
          lote.crear<Observation>({
            resourceType: 'Observation',
            status: 'final',
            category: [VITAL_SIGNS_CATEGORY],
            code: { coding: [{ system: 'http://loinc.org', code: loinc, display }] },
            subject,
            encounter,
            effectiveDateTime: ahora,
            valueQuantity: { value: valor, unit: unidad, system: 'http://unitsofmeasure.org', code: unidadUcum },
            component: component && component.length > 0 ? component : undefined,
          })
        );
        pares.push({ pregunta: etiqueta, respuesta: String(valor) });
      }
      if (componentPosicionTalla) pares.push({ pregunta: t('curvaCrecimiento.posicionTalla'), respuesta: t(`curvaCrecimiento.posicionTallaOpciones.${posicionTalla}`) });
      if (componentEdadGestacional) pares.push({ pregunta: t('curvaCrecimiento.edadGestacionalAlNacer'), respuesta: String(edadGestacionalAlNacer) });
      await lote.enviar(medplum);
      // Las curvas también van al PDF firmado: historial del paciente (con lo recién guardado) sobre la referencia. Ya está guardado: si esta lectura
      // falla, el PDF sale sin curvas pero no se reporta error (reintentar duplicaría las mediciones).
      const curvas = await buscarCurvasControlNinoPdf(medplum, subject, config.curvasPdf, textosCurvasPdf(config.curvasPdf, (clave) => t(clave)));
      onGuardado(pares, config.codigo ? datosEncabezadoMsp : undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, curvas);
    });

    return (
      <div className="flex flex-col gap-4">
        {config.codigo && <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />}
        {config.incluirEdadGestacionalAlNacer && (
          <div className="flex flex-col gap-1">
            <Label htmlFor={`curva-${config.claveI18n}-edad-gestacional`}>
              {t('curvaCrecimiento.edadGestacionalAlNacer')}
              {config.incluirEdadGestacionalAlNacer === 'requerido' ? ' *' : ''}
            </Label>
            <Input
              id={`curva-${config.claveI18n}-edad-gestacional`}
              type="number"
              value={edadGestacionalAlNacer}
              onChange={(e) => setEdadGestacionalAlNacer(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>
        )}

        <div className="flex flex-col gap-1">
          <Label htmlFor={`curva-${config.claveI18n}-peso`}>{t('curvaCrecimiento.peso')}</Label>
          <Input
            id={`curva-${config.claveI18n}-peso`}
            type="number"
            value={peso}
            onChange={(e) => setPeso(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor={`curva-${config.claveI18n}-talla`}>{t('curvaCrecimiento.talla')}</Label>
          <Input
            id={`curva-${config.claveI18n}-talla`}
            type="number"
            value={talla}
            onChange={(e) => setTalla(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </div>

        {config.incluirPosicionTalla && talla !== '' && (
          <div className="flex flex-col gap-1">
            <Label>{t('curvaCrecimiento.posicionTalla')}</Label>
            <RadioGroup value={posicionTalla} onValueChange={(v) => setPosicionTalla(v as 'acostado' | 'de-pie')}>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="acostado" id={`curva-${config.claveI18n}-posicion-acostado`} />
                <Label htmlFor={`curva-${config.claveI18n}-posicion-acostado`} className="font-normal">
                  {t('curvaCrecimiento.posicionTallaOpciones.acostado')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="de-pie" id={`curva-${config.claveI18n}-posicion-de-pie`} />
                <Label htmlFor={`curva-${config.claveI18n}-posicion-de-pie`} className="font-normal">
                  {t('curvaCrecimiento.posicionTallaOpciones.de-pie')}
                </Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {config.incluirPerimetroCefalico && (
          <div className="flex flex-col gap-1">
            <Label htmlFor={`curva-${config.claveI18n}-perimetro-cefalico`}>{t('curvaCrecimiento.perimetroCefalico')}</Label>
            <Input
              id={`curva-${config.claveI18n}-perimetro-cefalico`}
              type="number"
              value={perimetroCefalico}
              onChange={(e) => setPerimetroCefalico(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>
        )}

        {imcCalculado !== undefined && (
          <div className="flex flex-col gap-1">
            <Label htmlFor={`curva-${config.claveI18n}-imc`}>{t('curvaCrecimiento.imc')}</Label>
            <Input id={`curva-${config.claveI18n}-imc`} value={imcCalculado.toFixed(1)} disabled />
          </div>
        )}

        <ErrorGuardado error={error} />

        <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando || faltaEdadGestacionalRequerida}>
          {t('formulario.guardar')}
        </Button>
      </div>
    );
  };
}
